import { faAtomSimple, faUpDownLeftRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAnimationFrameEffect } from '@partful/features/instructions/animations/lib/useAnimationFrameEffect';
import { Button } from '@partful/shared/ui/Button';
import { Input } from '@partful/shared/ui/Input';
import { Surface } from '@partful/shared/ui/Surface';
import { Typography } from '@partful/shared/ui/Typography';
import { invalidate } from '@react-three/fiber';
import { ChangeEventHandler, useCallback, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { Material, Mesh } from 'three';
import { useModelEditor } from '../ModelEditor';
import styles from './ObjectPropertiesPanel.module.scss';

export function ObjectPropertiesPanel() {
  const { t: i18n } = useTranslation();
  const { selected } = useModelEditor();

  const visibleSelection = useMemo(() => selected?.filter((selected) => selected.visible) ?? [], [selected]);

  const calcOpacity = useCallback(
    () =>
      Math.round(
        visibleSelection.reduce((opacity, object) => {
          if (object instanceof Mesh && object.material instanceof Material && object.material.opacity < opacity) {
            return object.material.opacity;
          }

          return opacity;
        }, 1) * 100
      ).toFixed(0),
    [visibleSelection]
  );

  const [opacity, updateOpacity] = useReducer(calcOpacity, calcOpacity());
  useAnimationFrameEffect(() => {
    updateOpacity();
  });

  const handleResetPosition = useCallback(() => {
    selected?.forEach((selected) => {
      if (selected.userData.originalPosition) {
        const [x, y, z] = selected.userData.originalPosition.toArray();
        selected.position.set(x, y, z);
      }
    });
    invalidate();
  }, [selected]);

  const handleResetRotation = useCallback(() => {
    selected?.forEach((selected) => {
      if (selected?.userData.originalRotation) {
        const [x, y, z, w] = selected.userData.originalRotation.toArray();
        selected.quaternion.set(x, y, z, w);
      }
    });
    invalidate();
  }, [selected]);

  const handleOpacityChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      selected?.forEach((selected) => {
        if (selected instanceof Mesh && selected.material instanceof Material) {
          selected.material.opacity = Math.min(1, Math.max(0, Math.round(Number(e.target.value)) / 100));
          selected.material.transparent = true;
          invalidate();
        }
      });
      updateOpacity();
    },
    [selected]
  );

  if (visibleSelection.length === 0) return null;

  return (
    <Surface className={styles.propertiesPanel} shadow="large" data-testid="object-properties">
      <Typography
        className={styles.title}
        overflow="hidden"
        textOverflow="ellipsis"
        wordBreak="break-all"
        WebkitLineClamp={3}
        fontWeight={600}
      >
        {visibleSelection.map((selected) => selected.name || selected.id).join(', ')}
      </Typography>
      <div className={styles.position}>
        <Button size="small" variant="secondary" onClick={handleResetPosition}>
          <FontAwesomeIcon icon={faUpDownLeftRight} />
          {i18n('instructions:modelEditor.resetPosition')}
        </Button>
      </div>
      <div className={styles.rotation}>
        <Button size="small" variant="secondary" onClick={handleResetRotation}>
          <FontAwesomeIcon icon={faAtomSimple} />
          {i18n('instructions:modelEditor.resetRotation')}
        </Button>
      </div>
      <div className={styles.opacity}>
        <Typography
          as="label"
          htmlFor="opacity"
          appearance="label"
          overflow="hidden"
          textOverflow="ellipsis"
          fontWeight={600}
        >
          {i18n('instructions:modelEditor.opacity')}
        </Typography>
        <div className={styles.value}>
          <Input
            className={styles.slider}
            type="range"
            size="small"
            min={0}
            max={100}
            step={1}
            value={opacity}
            onChange={handleOpacityChange}
          />
          <Input
            className={styles.spinner}
            id="opacity"
            type="number"
            size="small"
            min={0}
            max={100}
            step={1}
            value={opacity}
            onChange={handleOpacityChange}
          />
          <span>%</span>
        </div>
      </div>
    </Surface>
  );
}
